import axios from 'axios'

const RAW_API_GATEWAY_URL = import.meta.env.VITE_API_URL || '/api'
const NORMALIZED_API_GATEWAY_URL = RAW_API_GATEWAY_URL.replace(/\/+$/, '')
const API_GATEWAY_URL = NORMALIZED_API_GATEWAY_URL.endsWith('/api/v1')
  ? NORMALIZED_API_GATEWAY_URL.slice(0, -3)
  : NORMALIZED_API_GATEWAY_URL.includes('/api')
    ? NORMALIZED_API_GATEWAY_URL
    : `${NORMALIZED_API_GATEWAY_URL}/api`
const API_GATEWAY_V1_URL = API_GATEWAY_URL.endsWith('/api')
  ? `${API_GATEWAY_URL}/v1`
  : API_GATEWAY_URL

const api = axios.create({
  baseURL: API_GATEWAY_URL,
  headers: { 'Content-Type': 'application/json' },
})

// Attach auth token automatically (similar to other services)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) {
    // Ensure Authorization header is set for gateway-protected routes
    (config.headers as any).Authorization = `Bearer ${token}`
  } else {
    console.warn('[payoutService] No auth token found in localStorage')
  }
  
  // For FormData, remove Content-Type header to let Axios set it with proper boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  
  return config
})

// Log 401 responses for easier debugging
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      console.warn('[payoutService] Received 401 from API gateway')
    }
    return Promise.reject(err)
  }
)

export interface Payout {
  id: string
  riderPublicID: string
  clientName: string
  beneficiaryName: string
  beneficiaryAccountNo: string
  ifsc: string
  transactionType: string
  debitAccountNo: string
  transactionDate: string
  amount: number
  remarks?: string | null
  utrNumber?: string | null
  status: 'SUCCESS' | 'FAILED' | 'PENDING' | string
  createdAt?: string
  updatedAt?: string
}

export const payoutService = {
  getAllPayouts: async (params?: {
    page?: number
    limit?: number
    status?: string
    clientName?: string
    fromDate?: string
    toDate?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) => {
    const query: Record<string, any> = {}
    if (params?.page) query.page = params.page
    if (params?.limit) query.limit = params.limit
    if (params?.status) query.status = params.status
    if (params?.clientName) query.clientName = params.clientName
    if (params?.fromDate) query.fromDate = params.fromDate
    if (params?.toDate) query.toDate = params.toDate
    if (params?.sortBy) query.sortBy = params.sortBy
    if (params?.sortOrder) query.sortOrder = params.sortOrder

    // Use v1 prefix so gateway routing stays consistent with backend
    try {
      const res = await api.get('/v1/payouts', { params: query })
      return res.data
    } catch (err: any) {
      // If gateway is missing the route (404), attempt to call the rider service directly (useful during local dev)
      if (err?.response?.status === 404) {
        try {
          const axiosInstance = (await import('axios')).default
          const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005'
          console.info(`[payoutService] Falling back to rider service at ${riderUrl}`)
          // Include auth token if present when calling rider service directly
          const token = localStorage.getItem('authToken')
          const headers: any = { 'Content-Type': 'application/json' }
          if (token) headers.Authorization = `Bearer ${token}`
          const direct = await axiosInstance.get(`${riderUrl}/api/v1/payouts`, { params: query, headers })
          return direct.data
        } catch (innerErr) {
          throw innerErr
        }
      }
      throw err
    }
  },

  getPayoutsByRider: async (riderPublicID: string, params?: { page?: number; limit?: number }) => {
    try {
      const res = await api.get(`/v1/payouts/${encodeURIComponent(riderPublicID)}`, { params })
      return res.data
    } catch (err: any) {
      if (err?.response?.status === 404) {
        try {
          const axiosInstance = (await import('axios')).default
          const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005'
          console.info(`[payoutService] Falling back to rider service at ${riderUrl}`)
          const token = localStorage.getItem('authToken')
          const headers: any = { 'Content-Type': 'application/json' }
          if (token) headers.Authorization = `Bearer ${token}`
          const direct = await axiosInstance.get(`${riderUrl}/api/v1/payouts/${encodeURIComponent(riderPublicID)}`, { params, headers })
          return direct.data
        } catch (innerErr) {
          throw innerErr
        }
      }
      throw err
    }
  },

  createPayout: async (payload: any) => {
    try {
      const res = await api.post('/v1/payouts', payload)
      return res.data
    } catch (err: any) {
      // If gateway denies the route, try rider service directly as a fallback (local dev)
      if (err?.response?.status === 404) {
        const axiosInstance = (await import('axios')).default
        const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005'
        console.info(`[payoutService] Falling back to rider service for create at ${riderUrl}`)
        const token = localStorage.getItem('authToken')
        const headers: any = { 'Content-Type': 'application/json' }
        if (token) headers.Authorization = `Bearer ${token}`
        const direct = await axiosInstance.post(`${riderUrl}/api/v1/payouts`, payload, { headers })
        return direct.data
      }
      throw err
    }
  },

  bulkUpload: async (file: File) => {
    try {
      const form = new FormData()
      form.append('file', file)
      // Note: Axios will set the correct multipart headers automatically when FormData is passed
      const res = await api.post('/v1/payouts/bulk-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // allow long uploads
        timeout: 5 * 60 * 1000,
      })
      return res.data
    } catch (err: any) {
      // Fallback to direct rider service when gateway route is missing
      if (err?.response?.status === 404) {
        const axiosInstance = (await import('axios')).default
        const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005'
        console.info(`[payoutService] Falling back to rider service for bulk upload at ${riderUrl}`)
        const token = localStorage.getItem('authToken')
        const headers: any = {}
        if (token) headers.Authorization = `Bearer ${token}`
        const form = new FormData()
        form.append('file', file)
        const direct = await axiosInstance.post(`${riderUrl}/api/v1/payouts/bulk-upload`, form, { headers })
        return direct.data
      }
      throw err
    }
  },

  generatePayoutFile: async (misFile: File, bankFile: File) => {
    const form = new FormData()
    form.append('misFile', misFile)
    form.append('bankFile', bankFile)

    const buildDownloadResult = (data: ArrayBuffer, headers: any) => {
      const contentType = headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const contentDisposition = headers['content-disposition'] || ''
      const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(contentDisposition)
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'merged-payout.xlsx'
      return {
        blob: new Blob([data], { type: contentType }),
        filename,
      }
    }

    // Use API gateway for file generation - gateway properly handles binary responses with arraybuffer
    const token = localStorage.getItem('authToken')
    const axiosInstance = (await import('axios')).default
    const headers: any = {}
    if (token) headers.Authorization = `Bearer ${token}`

    try {
      console.info(`[payoutService] Calling API gateway for payout file generation at ${API_GATEWAY_V1_URL}`)
      const res = await axiosInstance.post(`${API_GATEWAY_V1_URL}/payouts/generate`, form, {
        headers,
        responseType: 'arraybuffer',
        timeout: 5 * 60 * 1000,
      })
      return buildDownloadResult(res.data, res.headers)
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      err?.message ||
                      'Failed to generate payout file'
      const error: any = new Error(errorMsg)
      error.status = err?.response?.status
      throw error
    }
  },

  exportRiderBankDetails: async () => {
    const buildDownloadResult = (data: ArrayBuffer, headers: any) => {
      const contentType = headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const contentDisposition = headers['content-disposition'] || ''
      const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(contentDisposition)
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'rider-bank-details.xlsx'
      return {
        blob: new Blob([data], { type: contentType }),
        filename,
      }
    }

    const token = localStorage.getItem('authToken')
    const axiosInstance = (await import('axios')).default
    const headers: any = {}
    if (token) headers.Authorization = `Bearer ${token}`

    try {
      const res = await axiosInstance.get(`${API_GATEWAY_V1_URL}/riders/bank-details/export`, {
        headers,
        responseType: 'arraybuffer',
        timeout: 5 * 60 * 1000,
      })
      return buildDownloadResult(res.data, res.headers)
    } catch (err: any) {
      if (err?.response?.status === 404) {
        const axiosInstance = (await import('axios')).default
        const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005'
        console.info(`[payoutService] Falling back to rider service export at ${riderUrl}`)
        const token = localStorage.getItem('authToken')
        const headers: any = {}
        if (token) headers.Authorization = `Bearer ${token}`
        const direct = await axiosInstance.get(`${riderUrl}/api/v1/riders/bank-details/export`, {
          headers,
          responseType: 'arraybuffer',
          timeout: 5 * 60 * 1000,
        })
        return buildDownloadResult(direct.data, direct.headers)
      }

      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to export rider bank details'
      const error: any = new Error(errorMsg)
      error.status = err?.response?.status
      throw error
    }
  },
}

export default payoutService